import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminMe,
  adminLogout,
  adminListApplications,
  adminListContacts,
  adminGetApplication,
  adminDownloadFile,
  getAdminToken,
  adminListEvents, adminCreateEvent, adminUpdateEvent, adminDeleteEvent,
  adminListTestimonials, adminCreateTestimonial, adminUpdateTestimonial, adminDeleteTestimonial,
  adminUploadImage,
} from "../lib/api";
import { AdminLogin, ChangePassword } from "../components/admin/AdminAuth";
import { SensitivePanel, DeleteApplication } from "../components/admin/SensitivePanel";
import { TeamManager, AuditLog } from "../components/admin/TeamManager";
import { DeploySecretsHelper } from "../components/admin/DeploySecretsHelper";
import { LogOut, FileText, Download, Mail, User, Phone, ArrowLeft, Search, Calendar, MessageSquareQuote, Plus, Edit2, Trash2, Image as ImageIcon } from "lucide-react";
import { LOGO_URL, ORG } from "../lib/constants";

function Detail({ app, onBack, me, onDeleted }) {
  const renderObj = (obj) =>
    Object.entries(obj || {}).filter(([, v]) => v && (typeof v !== "object" || Object.keys(v).length));

  const groups = [
    ["Basic Info", {
      Name: app.name, Address: app.address, Phone: app.phone, Email: app.email,
      Ethnicity: app.ethnicity, "Marital status": app.marital_status, Referral: app.referral,
    }],
    ["Personal", {
      "Goals next year": app.goals_next_year, "How we can help": app.how_we_can_help,
      "Has children": app.has_children, "Number of children": app.num_children,
    }],
    ["Employment", {
      "Has job": app.has_job, Employer: app.employer,
      "Length of employment": app.length_of_employment, "Has transportation": app.has_transportation,
    }],
    ["Financial", {
      "Has entry fee": app.has_entry_fee, "Family support": app.family_support,
    }],
    ["Emergency Contact", {
      Name: app.emergency_name, Phone: app.emergency_phone, Relationship: app.emergency_relationship,
    }],
    ["Agreement", {
      Signature: app.signature, Date: app.signature_date,
      "Agreed to rules": app.agreed_to_rules ? "Yes" : "No",
    }],
  ];

  return (
    <div data-testid="admin-application-detail">
      <button
        onClick={onBack}
        data-testid="admin-back-btn"
        className="inline-flex items-center gap-2 text-brand-muted hover:text-brand-teal text-sm mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to list
      </button>
      <div className="bg-white rounded-3xl border border-brand-line p-6 md:p-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Application</div>
            <h2 className="font-serif text-3xl text-brand-charcoal mt-1">{app.name}</h2>
            <p className="text-xs text-brand-muted mt-1">
              Received {new Date(app.created_at).toLocaleString()}
            </p>
            <p className="text-[11px] text-brand-muted font-mono mt-1">Reference: {app.id.slice(0, 8).toUpperCase()}</p>
          </div>
          {me.role === "owner" && <DeleteApplication app={app} onDeleted={onDeleted} />}
        </div>

        <SensitivePanel app={app} />

        {groups.map(([title, fields]) => {
          const rows = renderObj(fields);
          if (!rows.length) return null;
          return (
            <div key={title} className="mt-8">
              <h3 className="font-serif text-lg text-brand-teal border-b border-brand-line pb-2 mb-3">{title}</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex gap-3">
                    <dt className="font-medium text-brand-muted min-w-[140px]">{k}</dt>
                    <dd className="text-brand-charcoal">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}

        {/* Housing */}
        {app.housing_history?.filter((h) => h.address || h.when || h.with_whom).length > 0 && (
          <div className="mt-8">
            <h3 className="font-serif text-lg text-brand-teal border-b border-brand-line pb-2 mb-3">Housing History</h3>
            <ul className="space-y-2 text-sm">
              {app.housing_history.map((h, i) => (h.address || h.when || h.with_whom) && (
                <li key={i} className="text-brand-charcoal">
                  <span className="font-medium">{h.address || "—"}</span>
                  <span className="text-brand-muted"> · {h.when || "—"}</span>
                  <span className="text-brand-muted"> · with {h.with_whom || "—"}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Known contacts */}
        {app.known_contacts?.filter((c) => c.name || c.phone).length > 0 && (
          <div className="mt-8">
            <h3 className="font-serif text-lg text-brand-teal border-b border-brand-line pb-2 mb-3">Local Connections</h3>
            <ul className="space-y-2 text-sm">
              {app.known_contacts.map((c, i) => (c.name || c.phone) && (
                <li key={i} className="text-brand-charcoal">
                  <span className="font-medium">{c.name}</span> ({c.relation}) · {c.address} · {c.phone}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* References */}
        {app.references?.filter((r) => r.name).length > 0 && (
          <div className="mt-8">
            <h3 className="font-serif text-lg text-brand-teal border-b border-brand-line pb-2 mb-3">References</h3>
            <ul className="space-y-2 text-sm">
              {app.references.map((r, i) => r.name && (
                <li key={i} className="text-brand-charcoal">
                  <span className="font-medium">{r.name}</span>{[r.phone, r.relationship].filter(Boolean).map((x) => ` · ${x}`).join("")}{r.years_known ? ` (${r.years_known})` : ""}
                  <div className="text-brand-muted text-xs">{r.address}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Uploads */}
        {app.uploads?.length > 0 && (
          <div className="mt-8">
            <h3 className="font-serif text-lg text-brand-teal border-b border-brand-line pb-2 mb-3">Uploaded Documents</h3>
            <ul className="space-y-2">
              {app.uploads.map((u, i) => (
                <li key={i} className="flex items-center justify-between gap-3 bg-brand-cream border border-brand-line rounded-xl px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-brand-teal shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-brand-charcoal truncate">{u.filename}</div>
                      <div className="text-[11px] text-brand-muted">
                        {u.category?.toUpperCase()} · {(u.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => adminDownloadFile(u.path, u.filename).catch(() => toast.error("Download failed"))}
                    aria-label={`Download ${u.filename || "document"}`}
                    data-testid={`admin-download-${i}`}
                    className="inline-flex items-center gap-1.5 text-sm text-brand-teal hover:text-brand-tealDark font-medium"
                  >
                    <Download className="w-4 h-4" /> Download
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Dashboard({ onLogout, me }) {
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c, e, t] = await Promise.all([
        adminListApplications(),
        adminListContacts(),
        adminListEvents(),
        adminListTestimonials(),
      ]);
      setApps(a); setContacts(c); setEvents(e); setTestimonials(t);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Could not load data");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filteredApps = apps.filter(
    (a) => !query || `${a.name} ${a.phone} ${a.email}`.toLowerCase().includes(query.toLowerCase())
  );
  const filteredContacts = contacts.filter(
    (c) => !query || `${c.name} ${c.email} ${c.subject} ${c.message}`.toLowerCase().includes(query.toLowerCase())
  );

  if (selected) {
    return (
      <div className="min-h-screen bg-brand-cream">
        <TopBar onLogout={onLogout} me={me} />
        <main className="container-editorial py-10">
          <Detail app={selected} me={me} onBack={() => setSelected(null)} onDeleted={() => { setSelected(null); load(); }} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream">
      <TopBar onLogout={onLogout} me={me} />
      <main className="container-editorial py-10" data-testid="admin-dashboard">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <StatCard icon={FileText} label="Applications" value={apps.length} />
          <StatCard icon={Mail} label="Messages" value={contacts.length} />
          <StatCard
            icon={User}
            label="Applied this month"
            value={apps.filter((a) => {
              const d = new Date(a.created_at);
              const n = new Date();
              return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
            }).length}
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-brand-line mb-5 overflow-x-auto">
          {[
            { k: "applications", label: "Applications", count: apps.length },
            { k: "contacts", label: "Messages", count: contacts.length },
            { k: "events", label: "Events", count: events.length },
            { k: "testimonials", label: "Testimonials", count: testimonials.length },
            ...(me.role === "owner" ? [{ k: "team", label: "Team" }, { k: "activity", label: "Activity" }, { k: "deploy", label: "Deploy setup" }] : []),
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              aria-pressed={tab === t.k}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                tab === t.k ? "text-brand-teal border-brand-teal" : "text-brand-muted border-transparent hover:text-brand-charcoal"
              }`}
            >
              {t.label} {t.count !== undefined && <span className="ml-2 text-[11px] text-brand-muted">({t.count})</span>}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
            <input
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              aria-label="Search"
              data-testid="admin-search"
              className="bg-white border border-brand-line rounded-full pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/25"
            />
          </div>
        </div>

        {loading && <div className="text-center text-brand-muted py-20">Loading…</div>}

        {!loading && tab === "applications" && (
          <div className="bg-white rounded-2xl border border-brand-line overflow-hidden">
            {filteredApps.length === 0 ? (
              <div className="text-center text-brand-muted py-20" data-testid="admin-apps-empty">{query ? "No applications match your search." : "No applications yet."}</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-brand-cream">
                  <tr className="text-left text-[11px] tracking-widest uppercase text-brand-muted">
                    <th className="px-5 py-3">Applicant</th>
                    <th className="px-5 py-3">Phone</th>
                    <th className="px-5 py-3 hidden md:table-cell">Received</th>
                    <th className="px-5 py-3 hidden md:table-cell">Docs</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((a) => (
                    <tr key={a.id} className="border-t border-brand-line hover:bg-brand-cream/40 transition-colors">
                      <td className="px-5 py-4">
                        <div className="font-medium text-brand-charcoal">{a.name}</div>
                        <div className="text-xs text-brand-muted">{a.email || "—"}</div>
                      </td>
                      <td className="px-5 py-4 text-brand-muted">{a.phone || "—"}</td>
                      <td className="px-5 py-4 hidden md:table-cell text-brand-muted">
                        {new Date(a.created_at).toLocaleString()}
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        {a.uploads_count ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-brand-teal/10 text-brand-teal rounded-full px-2.5 py-1">
                            <FileText className="w-3 h-3" /> {a.uploads_count}
                          </span>
                        ) : (
                          <span className="text-xs text-brand-muted">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => adminGetApplication(a.id).then(setSelected).catch(() => toast.error("Could not open"))}
                          data-testid={`admin-view-${a.id}`}
                          aria-label={`View application from ${a.name}`}
                          className="text-brand-teal hover:text-brand-tealDark font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!loading && tab === "contacts" && (
          <div className="bg-white rounded-2xl border border-brand-line overflow-hidden">
            {filteredContacts.length === 0 ? (
              <div className="text-center text-brand-muted py-20">{query ? "No messages match your search." : "No messages yet."}</div>
            ) : (
              <ul>
                {filteredContacts.map((c) => (
                  <li key={c.id} className="border-t border-brand-line first:border-t-0 px-5 py-4 hover:bg-brand-cream/40 transition-colors">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-medium text-brand-charcoal">{c.name} <span className="text-xs text-brand-muted font-normal">&lt;{c.email}&gt;</span></div>
                        <div className="text-xs text-brand-muted mt-0.5 flex flex-wrap items-center gap-3">
                          {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                          <span>{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                      {c.subject && <div className="text-sm text-brand-teal font-medium">{c.subject}</div>}
                    </div>
                    <p className="mt-2 text-sm text-brand-charcoal whitespace-pre-wrap">{c.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!loading && tab === "events" && (
          <EventsManager
            events={events.filter((e) => !query || `${e.title} ${e.location}`.toLowerCase().includes(query.toLowerCase()))}
            onEdit={setEditingEvent}
            onCreate={() => setEditingEvent({})}
            onDelete={async (id) => {
              if (!window.confirm("Delete this event?")) return;
              try { await adminDeleteEvent(id); toast.success("Deleted"); load(); }
              catch { toast.error("Delete failed"); }
            }}
          />
        )}

        {!loading && tab === "team" && <TeamManager me={me} />}
        {!loading && tab === "activity" && <AuditLog />}
        {!loading && tab === "deploy" && <DeploySecretsHelper />}

        {!loading && tab === "testimonials" && (
          <TestimonialsManager
            items={testimonials.filter((t) => !query || `${t.name} ${t.message}`.toLowerCase().includes(query.toLowerCase()))}
            onEdit={setEditingTestimonial}
            onCreate={() => setEditingTestimonial({})}
            onDelete={async (id) => {
              if (!window.confirm("Delete this testimonial?")) return;
              try { await adminDeleteTestimonial(id); toast.success("Deleted"); load(); }
              catch { toast.error("Delete failed"); }
            }}
          />
        )}
      </main>

      {editingEvent !== null && (
        <EventEditor
          initial={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSaved={() => { setEditingEvent(null); load(); }}
        />
      )}
      {editingTestimonial !== null && (
        <TestimonialEditor
          initial={editingTestimonial}
          onClose={() => setEditingTestimonial(null)}
          onSaved={() => { setEditingTestimonial(null); load(); }}
        />
      )}
    </div>
  );
}

const inputCls = "w-full bg-brand-cream border border-brand-line rounded-xl px-4 py-2.5 text-sm text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal/25 focus:border-brand-teal transition-all";
const lblCls = "block text-xs font-medium text-brand-muted mb-1.5 tracking-wide";

function EventsManager({ events, onEdit, onCreate, onDelete }) {
  return (
    <div data-testid="events-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-brand-muted">Manage upcoming events shown on the home page and as the popup announcement.</p>
        <button onClick={onCreate} data-testid="event-new-btn" className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-tealDark transition">
          <Plus className="w-4 h-4" /> New event
        </button>
      </div>
      {events.length === 0 ? (
        <div className="text-center text-brand-muted bg-white border border-brand-line rounded-2xl py-16">No events yet.</div>
      ) : (
        <ul className="space-y-3">
          {events.map((e) => (
            <li key={e.id} className="bg-white border border-brand-line rounded-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 min-w-0">
                <span className="w-11 h-11 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0">
                  <Calendar className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <div className="font-medium text-brand-charcoal truncate">
                    {e.title}
                    {e.is_popup && <span className="ml-2 text-[10px] tracking-widest uppercase bg-brand-rose/10 text-brand-rose px-2 py-0.5 rounded-full">Popup</span>}
                    {!e.is_active && <span className="ml-2 text-[10px] tracking-widest uppercase bg-brand-line text-brand-muted px-2 py-0.5 rounded-full">Hidden</span>}
                  </div>
                  <div className="text-xs text-brand-muted truncate">{[e.date_label, e.time_label, e.location].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => onEdit(e)} data-testid={`event-edit-${e.id}`} className="text-sm text-brand-teal hover:text-brand-tealDark inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-brand-cream"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => onDelete(e.id)} data-testid={`event-delete-${e.id}`} className="text-sm text-brand-rose hover:text-red-700 inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-brand-rose/10"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EventEditor({ initial, onClose, onSaved }) {
  const editing = !!initial.id;
  const [data, setData] = useState({
    title: initial.title || "",
    description: initial.description || "",
    date_label: initial.date_label || "",
    time_label: initial.time_label || "",
    location: initial.location || "",
    rsvp_url: initial.rsvp_url || "",
    image_path: initial.image_path || "",
    image_filename: initial.image_filename || "",
    image_url: initial.image_url || "",
    is_popup: initial.is_popup ?? false,
    is_active: initial.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const onPick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Max 10 MB");
    setUploading(true);
    try {
      const r = await adminUploadImage(f, "event");
      setData((d) => ({ ...d, image_path: r.path, image_filename: r.filename, image_url: "" }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!data.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      if (editing) await adminUpdateEvent(initial.id, data);
      else await adminCreateEvent(data);
      toast.success(editing ? "Updated" : "Created");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-3xl shadow-2xl border border-brand-line w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-editor">
        <div className="px-6 py-5 border-b border-brand-line flex items-center justify-between">
          <h3 className="font-serif text-xl text-brand-charcoal">{editing ? "Edit event" : "New event"}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-brand-muted hover:text-brand-charcoal">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="ed-title" className={lblCls}>Title</label>
            <input id="ed-title" className={inputCls} value={data.title} onChange={set("title")} data-testid="event-title" autoFocus />
          </div>
          <div>
            <label htmlFor="ed-description" className={lblCls}>Description</label>
            <textarea id="ed-description" rows={3} className={inputCls} value={data.description} onChange={set("description")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label htmlFor="ed-date-label" className={lblCls}>Date label</label><input id="ed-date-label" className={inputCls} placeholder="May 2, 2026" value={data.date_label} onChange={set("date_label")} /></div>
            <div><label htmlFor="ed-time-label" className={lblCls}>Time label</label><input id="ed-time-label" className={inputCls} placeholder="1:00 PM – 3:00 PM" value={data.time_label} onChange={set("time_label")} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label htmlFor="ed-location" className={lblCls}>Location</label><input id="ed-location" className={inputCls} value={data.location} onChange={set("location")} /></div>
            <div><label htmlFor="ed-rsvp-url" className={lblCls}>RSVP URL</label><input id="ed-rsvp-url" className={inputCls} placeholder="https://eventbrite.com/…" value={data.rsvp_url} onChange={set("rsvp_url")} /></div>
          </div>

          <div>
            <span className={lblCls}>Cover image</span>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-2 bg-brand-cream border border-brand-line rounded-xl px-4 py-2.5 text-sm hover:border-brand-teal">
                <ImageIcon className="w-4 h-4 text-brand-teal" />
                {uploading ? "Uploading…" : "Upload image"}
                <input type="file" accept="image/*" className="hidden" onChange={onPick} disabled={uploading} />
              </label>
              {(data.image_path || data.image_url) && (
                <span className="text-xs text-brand-muted truncate max-w-[260px]">{data.image_filename || data.image_url}</span>
              )}
            </div>
            <div className="mt-3">
              <label htmlFor="ed-or-external-image-url" className={lblCls}>Or external image URL</label>
              <input id="ed-or-external-image-url" className={inputCls} placeholder="https://…" value={data.image_url} onChange={(e) => setData((d) => ({ ...d, image_url: e.target.value, image_path: "" }))} />
            </div>
          </div>

          <div className="flex items-center gap-6 pt-2">
            <label className="inline-flex items-center gap-2 text-sm text-brand-charcoal cursor-pointer">
              <input type="checkbox" checked={data.is_popup} onChange={set("is_popup")} data-testid="event-popup" className="w-4 h-4" />
              Show as site popup announcement
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-brand-charcoal cursor-pointer">
              <input type="checkbox" checked={data.is_active} onChange={set("is_active")} data-testid="event-active" className="w-4 h-4" />
              Active (visible)
            </label>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-brand-line flex items-center justify-end gap-3 bg-brand-cream/40">
          <button type="button" onClick={onClose} className="text-sm text-brand-muted hover:text-brand-charcoal px-3 py-2">Cancel</button>
          <button type="submit" disabled={saving} data-testid="event-save" className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-brand-tealDark disabled:opacity-60">
            {saving ? "Saving…" : (editing ? "Save changes" : "Create event")}
          </button>
        </div>
      </form>
    </div>
  );
}

function TestimonialsManager({ items, onEdit, onCreate, onDelete }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  return (
    <div data-testid="testimonials-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-brand-muted">Stories shared on the public Stories page.</p>
        <button onClick={onCreate} data-testid="testimonial-new-btn" className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-tealDark transition">
          <Plus className="w-4 h-4" /> New testimonial
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-center text-brand-muted bg-white border border-brand-line rounded-2xl py-16">No testimonials yet.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((t) => {
            const imgSrc = t.image_url ? (t.image_url.startsWith("http") ? t.image_url : `${BACKEND_URL}${t.image_url}`) : null;
            return (
              <li key={t.id} className="bg-white border border-brand-line rounded-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-brand-bone overflow-hidden flex items-center justify-center shrink-0">
                    {imgSrc ? <img src={imgSrc} alt={t.name} className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-brand-muted" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-brand-charcoal">{t.name} {!t.is_active && <span className="ml-2 text-[10px] tracking-widest uppercase bg-brand-line text-brand-muted px-2 py-0.5 rounded-full">Hidden</span>}</div>
                    <div className="text-xs text-brand-muted line-clamp-1">{t.message}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => onEdit(t)} className="text-sm text-brand-teal hover:text-brand-tealDark inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-brand-cream"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => onDelete(t.id)} className="text-sm text-brand-rose hover:text-red-700 inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-brand-rose/10"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TestimonialEditor({ initial, onClose, onSaved }) {
  const editing = !!initial.id;
  const [data, setData] = useState({
    name: initial.name || "",
    role: initial.role || "",
    message: initial.message || "",
    image_path: initial.image_path || "",
    image_filename: initial.image_filename || "",
    image_url: initial.image_url || "",
    is_active: initial.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const onPick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Max 10 MB");
    setUploading(true);
    try {
      const r = await adminUploadImage(f, "testimonial");
      setData((d) => ({ ...d, image_path: r.path, image_filename: r.filename, image_url: "" }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!data.name.trim() || !data.message.trim()) return toast.error("Name and message are required");
    setSaving(true);
    try {
      if (editing) await adminUpdateTestimonial(initial.id, data);
      else await adminCreateTestimonial(data);
      toast.success(editing ? "Updated" : "Created");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-3xl shadow-2xl border border-brand-line w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="testimonial-editor">
        <div className="px-6 py-5 border-b border-brand-line flex items-center justify-between">
          <h3 className="font-serif text-xl text-brand-charcoal">{editing ? "Edit testimonial" : "New testimonial"}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-brand-muted hover:text-brand-charcoal">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label htmlFor="ed-name" className={lblCls}>Name</label><input id="ed-name" className={inputCls} value={data.name} onChange={set("name")} autoFocus /></div>
            <div><label htmlFor="ed-role-context" className={lblCls}>Role / context</label><input id="ed-role-context" className={inputCls} placeholder="Graduate · 2025" value={data.role} onChange={set("role")} /></div>
          </div>
          <div>
            <label htmlFor="ed-message" className={lblCls}>Message</label>
            <textarea id="ed-message" rows={5} className={inputCls} value={data.message} onChange={set("message")} />
          </div>
          <div>
            <span className={lblCls}>Photo</span>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-2 bg-brand-cream border border-brand-line rounded-xl px-4 py-2.5 text-sm hover:border-brand-teal">
                <ImageIcon className="w-4 h-4 text-brand-teal" />
                {uploading ? "Uploading…" : "Upload photo"}
                <input type="file" accept="image/*" className="hidden" onChange={onPick} disabled={uploading} />
              </label>
              {(data.image_path || data.image_url) && (
                <span className="text-xs text-brand-muted truncate max-w-[260px]">{data.image_filename || data.image_url}</span>
              )}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-brand-charcoal cursor-pointer">
            <input type="checkbox" checked={data.is_active} onChange={set("is_active")} className="w-4 h-4" /> Active (visible)
          </label>
        </div>
        <div className="px-6 py-4 border-t border-brand-line flex items-center justify-end gap-3 bg-brand-cream/40">
          <button type="button" onClick={onClose} className="text-sm text-brand-muted hover:text-brand-charcoal px-3 py-2">Cancel</button>
          <button type="submit" disabled={saving} data-testid="testimonial-save" className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-brand-tealDark disabled:opacity-60">
            {saving ? "Saving…" : (editing ? "Save changes" : "Create testimonial")}
          </button>
        </div>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-line p-5 flex items-center gap-4">
      <span className="w-11 h-11 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <div className="font-serif text-3xl text-brand-charcoal">{value}</div>
        <div className="text-xs uppercase tracking-wider text-brand-muted">{label}</div>
      </div>
    </div>
  );
}

function TopBar({ onLogout, me }) {
  return (
    <header className="bg-white border-b border-brand-line sticky top-0 z-30">
      <div className="container-editorial flex items-center justify-between py-4">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
          <div>
            <div className="font-serif text-lg text-brand-charcoal">Admin Portal</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-brand-rose">{ORG.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline text-xs text-brand-muted" data-testid="admin-current-user">{me.email}</span>
          <a href="/" className="text-sm text-brand-muted hover:text-brand-teal link-underline">View site</a>
          <button
            onClick={onLogout} data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-charcoal"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

export default function AdminPage() {
  const [me, setMe] = useState(undefined);

  useEffect(() => {
    document.title = "Admin · Hosea Ministries Foundation";
    const robots = document.createElement("meta");
    robots.name = "robots"; robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    window.posthog?.stopSessionRecording?.();
    if (getAdminToken()) adminMe().then(setMe).catch(() => setMe(null));
    else setMe(null);
    return () => robots.remove();
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (me && getAdminToken()) adminMe().catch(() => { toast.error("Session expired. Please sign in again."); setMe(null); });
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [me]);

  const logout = () => adminLogout().finally(() => setMe(null));

  let content;
  if (me === undefined) content = <div className="min-h-screen flex items-center justify-center text-brand-muted">Loading…</div>;
  else if (!me) content = <AdminLogin onAuthed={setMe} />;
  else if (me.must_change_password) content = <ChangePassword onDone={() => adminMe().then(setMe)} />;
  else content = <Dashboard me={me} onLogout={logout} />;
  return <div className="ph-no-capture" data-private="true">{content}</div>;
}
